import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "./i18n";

/**
 * Opens a print-optimized magazine layout in a new tab (via localStorage).
 * Then MagazinePrint reads from localStorage and renders.
 * The current UI language is stashed alongside so the print page can render
 * localized section titles without another network round-trip.
 */
export function openMagazinePrint(trip) {
  const id = "print_" + Date.now().toString(36);
  const lang = (localStorage.getItem("taip_lang") === "en") ? "en" : "ms";
  localStorage.setItem("taip_print_" + id, JSON.stringify({ trip, lang }));
  const w = window.open(`/print/${id}`, "_blank");
  if (!w) alert(lang === "en"
    ? "Please allow pop-ups to generate the PDF."
    : "Sila benarkan pop-up untuk menjana PDF.");
}

export default function MagazinePrint() {
  const location = useLocation();
  const { t } = useT();
  const parts = location.pathname.split("/");
  const id = parts[parts.length - 1];
  const raw = localStorage.getItem("taip_print_" + id);
  // Backwards-compatible: older prints stored the trip directly, not
  // wrapped in {trip, lang}. Handle both.
  let trip = null;
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      trip = parsed?.trip || parsed;
    } catch { trip = null; }
  }

  useEffect(() => {
    if (trip) {
      setTimeout(() => window.print(), 700);
    }
  }, [trip]);

  if (!trip) {
    return (
      <div className="p-10 text-center text-gray-500">
        {t("pdf.no_trip")}
      </div>
    );
  }

  const total = trip.budget_breakdown?.total ?? trip.budget_myr ?? 0;
  const halalLabel = (s) =>
    s === "confirmed" ? t("pdf.halal_confirmed")
      : s === "need_verify" ? t("pdf.halal_verify")
      : t("pdf.halal_unknown");

  return (
    <div className="bg-white text-[#1D1D1F]">
      {/* Cover */}
      <section className="print-page min-h-screen p-10 flex flex-col justify-between bg-gradient-to-br from-[#0A2540] to-[#15374E] text-white">
        <div>
          <div className="text-xs tracking-[0.4em] uppercase text-[#4FB3E8]">{t("pdf.magazine")}</div>
          <div className="mt-2 text-sm text-white/70">Plan Smarter. Travel Better.</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#4FB3E8] mb-3">{t("pdf.destination")}</div>
          <h1 className="text-6xl font-semibold tracking-tight">{trip.destination}</h1>
          <p className="text-white/80 mt-1 text-lg">{trip.country} · {trip.days} {t("iv.days")} · {trip.party}</p>
          {trip.summary && <p className="mt-6 max-w-xl text-white/85">{trip.summary}</p>}
        </div>
        <div className="text-xs text-white/50">{t("iv.estimated_budget")} · RM{total.toLocaleString()}</div>
      </section>

      {/* Itinerary */}
      <section className="print-page p-10">
        <h2 className="text-3xl font-semibold text-[#0A2540] mb-6">{t("pdf.itinerary")}</h2>
        <div className="space-y-6">
          {(trip.days_plan || []).map((d, i) => (
            <div key={i}>
              <div className="text-xs tracking-[0.15em] uppercase text-[#15C3C6] font-semibold">{t("pdf.days_label")} {d.day}</div>
              <div className="text-xl font-semibold text-[#0A2540] mb-2">{d.title}</div>
              <ul className="space-y-1.5">
                {(d.items || []).map((it, j) => (
                  <li key={j} className="text-sm">
                    <span className="text-gray-500 mr-2">{it.time}</span>
                    <span className="font-medium">{it.place}</span>
                    {it.note && <span className="text-gray-500"> — {it.note}</span>}
                    {it.cost_myr > 0 && <span className="ml-2 text-[#0A2540] font-medium">RM{it.cost_myr}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Budget & Checklist */}
      <section className="print-page p-10 grid grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-semibold text-[#0A2540] mb-4">{t("pdf.budget")}</h2>
          {trip.budget_breakdown && Object.entries(trip.budget_breakdown).filter(([k]) => k !== "total").map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm py-1 border-b border-gray-100">
              <span>{t(`budget.${k}`) || k}</span><span>RM{Number(v).toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between font-semibold mt-3 text-[#0A2540]">
            <span>{t("pdf.total")}</span><span>RM{total.toLocaleString()}</span>
          </div>
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-[#0A2540] mb-4">{t("pdf.checklist")}</h2>
          <ul className="text-sm space-y-1.5">
            {(trip.checklist || []).map((c, i) => (
              <li key={i} className="flex gap-2"><span>☐</span>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Hotels & Restaurants */}
      <section className="print-page p-10">
        <h2 className="text-2xl font-semibold text-[#0A2540] mb-4">{t("pdf.hotels_restaurants")}</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm uppercase tracking-[0.15em] text-[#15C3C6] mb-2">{t("pdf.hotel")}</h3>
            {(trip.hotels || []).map((h, i) => (
              <div key={i} className="mb-3 pb-3 border-b border-gray-100">
                <div className="font-medium">{h.name}</div>
                <div className="text-xs text-gray-500">{h.area} · RM{h.price_myr_per_night}{t("pdf.per_night")} · ★ {h.rating}</div>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-sm uppercase tracking-[0.15em] text-[#15C3C6] mb-2">{t("pdf.restaurant")}</h3>
            {(trip.restaurants || []).map((r, i) => (
              <div key={i} className="mb-3 pb-3 border-b border-gray-100">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-gray-500">{r.cuisine} · RM{r.price_myr} · {halalLabel(r.halal_status)}</div>
                {r.note && <div className="text-xs text-gray-500 mt-0.5">{r.note}</div>}
              </div>
            ))}
          </div>
        </div>
        {trip.tips?.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-2">{t("pdf.tips")}</h3>
            <ul className="text-sm space-y-1">
              {trip.tips.map((tip, i) => <li key={i}>· {tip}</li>)}
            </ul>
          </div>
        )}
        <div className="text-[10px] text-gray-400 mt-10 border-t border-gray-100 pt-3">
          {t("pdf.footer_note")}
        </div>
      </section>
    </div>
  );
}
